<template>
  <ErrorModal />
  <div class="chat-history-popup">
    <DxPopup
      v-model:visible="isVisible"
      :show-title="true"
      :drag-enabled="false"
      :hide-on-outside-click="false"
      :title="t('chat.chat_history_popup_title')"
      :show-close-button="true"
      @hiding="closePopup"
      width="94vw"
      height="96vh"
      :wrapper-attr="{ class: 'history-popup' }"
    >
      <DxSplitter orientation="horizontal" class="popup-splitter popup-content">
        <DxSplitterItem size="240px" min-size="200px" max-size="380px">
          <div class="popup-padding chats-list">
            <div
              class="chat-item-container"
              v-for="conversation in conversations"
              :key="conversation.conversationId"
            >
              <div class="chat-item">
                <div class="chat-item-date">
                  {{ formatDate(conversation.conversationStartedAt) }}
                </div>

                <div
                  class="chat-item-topic"
                  :id="`topicFullText-${conversation.conversationId}`"
                  @click="getMessageByConvoId(conversation.conversationId)"
                >
                  {{ conversation.shortTopic }}
                </div>
                <DxTooltip
                  v-if="
                    conversation.shortTopic != conversation.conversationTopic
                  "
                  :hide-on-outside-click="false"
                  :target="`#topicFullText-${conversation.conversationId}`"
                  show-event="mouseenter"
                  hide-event="mouseleave"
                  position="top"
                  :container="'body'"
                >
                  {{ conversation.conversationTopic }}
                </DxTooltip>
              </div>
            </div>
          </div>
        </DxSplitterItem>

        <DxSplitterItem>
          <div class="popup-padding chat-history-container">
            <DxChat
              :items="messages"
              :user="currentUser"
              :showAvatar="true"
              :showUserName="true"
              :showDayHeaders="true"
              :showMessageTimestamp="true"
              :dayHeaderFormat="'dd/MM/yyyy'"
              :messageTimestampFormat="'hh:mm a'"
              message-template="message"
              class="chat-content"
              empty-view-template="empty-view-template"
              @attachment-download-click="onAttachmentDownloadClick"
              ><template #message="{ data }">
                <div
                  :class="[
                    'chat-message-content',
                    {
                      'chat-message-attachment-download-expired':
                        isAttachmentDownloadExpired(data.message),
                    },
                  ]"
                >
                  <div
                    class="chat-message-text"
                    v-html="renderMessage(data.message)"
                  ></div>
                  <div
                    v-if="isAttachmentDownloadExpired(data.message)"
                    class="chat-attachment-download-note"
                  >
                    {{
                      t("chat.attachment_download_period_notice", {
                        days: attachmentDownloadAvailableDays,
                      })
                    }}
                  </div>
                </div>
              </template>
              <template #empty-view-template="{}">
                <div>{{ t("chat.chat_empty") }}</div>
              </template>
            </DxChat>

            <div class="continue-btn-container row justify-end">
              <DxButton
                :text="t('chat.continue_conversation')"
                class="orange-btn-filled continue-chat-btn"
                @click="continueConversation"
              />
            </div>
          </div>
        </DxSplitterItem>
      </DxSplitter>
    </DxPopup>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted } from "vue";
import store from "../../store/index";
import MarkdownIt from "markdown-it";
import moment from "moment";
import { DxPopup } from "devextreme-vue/popup";
import { DxTooltip } from "devextreme-vue/tooltip";

import DxSplitter, { DxItem as DxSplitterItem } from "devextreme-vue/splitter";
import { DxChat } from "devextreme-vue/chat";
import { DxButton } from "devextreme-vue/button";

import AiAssistantService from "../../services/aiAssistant.service";
import ErrorModal from "../../views/ErrorModal.vue";
import { reloadChatDataSource } from "../../store/modules/custom.store";
import aiAssistantService from "../../services/aiAssistant.service";
import { useI18n } from "vue-i18n";

onMounted(async () => {
  await AiAssistantService.getUserConversations();
  if (
    store.state.chat.currentConversationIdForHistory &&
    store.state.chat.currentConversationIdForHistory.length > 0
  )
    getMessageByConvoId(store.state.chat.currentConversationIdForHistory);
});
const md = new MarkdownIt({
  html: true,
  linkify: true,
  breaks: true,
});

const { t } = useI18n();
const renderMessage = (message) => {
  if (!message || !message.text) return "";

  return md.render(message.text);
};

const conversations = computed({
  get: () => store.state.chat.chatHistoryMessages,
});

const isVisible = computed({
  get: () => store.state.chat.isHistoryPopupVisible,
});

const closePopup = () => {
  store.commit("chat/updateIsHistoryPopupVisible", false);
  store.commit("chat/updateShowChat", true);
};

const currentUser = computed({
  get: () => store.state.chat.currentUser,
});

const attachmentDownloadAvailableDays = 60;

const isAttachmentDownloadAvailable = (timestamp) => {
  const messageTimestamp = moment(timestamp);

  return (
    messageTimestamp.isValid() &&
    !messageTimestamp.isBefore(
      moment().subtract(attachmentDownloadAvailableDays, "days"),
    )
  );
};

const addAttachmentDownloadAvailability = (message) => {
  const isDownloadAvailable = isAttachmentDownloadAvailable(message.timestamp);

  return {
    ...message,
    isAttachmentDownloadAvailable: isDownloadAvailable,
    attachments: (message.attachments ?? []).map((attachment) => ({
      ...attachment,
      isDownloadAvailable,
    })),
  };
};

const isAttachmentDownloadExpired = (message) =>
  Array.isArray(message?.attachments) &&
  message.attachments.length > 0 &&
  !message.isAttachmentDownloadAvailable;

const messages = computed(() =>
  (store.state.chat.messagesFromSelectedConversation ?? []).map(
    addAttachmentDownloadAvailability,
  ),
);

const getMessageByConvoId = async (id) => {
  await AiAssistantService.getMessagesByConversation(id);
  store.commit("chat/setCurrentConversationIdForHistory", id);
};

const formatDate = (date) => {
  return moment(date).format("DD-MM-YYYY");
};

const continueConversation = async () => {
  store.commit("chat/continueChatFromHistory");
  closePopup();
  await nextTick();
  await reloadChatDataSource();
};

const ensureAssistantSession = async () => {
  if (
    !store.state.chat.aiAssistantAccessToken.accessToken ||
    moment(store.state.chat.aiAssistantAccessToken.expirationTime).isBefore(
      moment().add(1, "minute"),
    )
  ) {
    const response = await AiAssistantService.getAIAssistantAccessToken();

    if (response.status === 200) {
      await store.dispatch(
        "chat/setAIAssistantAccessToken",
        response.data,
        t("chat.error_occurred"),
      );
    }
  }
};

const onAttachmentDownloadClick = async (event) => {
  if (!event.attachment?.isDownloadAvailable) return;

  await ensureAssistantSession();
  await downloadAttachment(
    event.attachment.attachmentId,
    event.attachment.name,
  );
};

const downloadAttachment = async (attachmentId, name) => {
  await aiAssistantService
    .downloadAttachment(attachmentId)
    .then((response) => {
      const fileURL = window.URL.createObjectURL(
        new Blob([response.data], { type: response.data.type }),
      );
      let a = document.createElement("a");
      a.setAttribute("style", "display:none;");
      a.download = name;
      a.href = fileURL;
      a.target = "_blank";
      a.click();
    })
    .catch(async (error) => {
      const data = error.response?.data;

      if (data instanceof Blob) {
        const message = await data.text();
        store.commit("user/updateModalError", message);
      } else store.commit("user/updateModalError", error.message);

      store.commit("user/enableModalError");
    });
};
</script>

<style scoped lang="scss">
@use "src/content/sass/base/defaultsettings" as *;
.popup-content {
  display: flex;
  .chats-list {
    display: flex;
    flex-direction: column;
    .chat-item-container:nth-child(odd) {
      background-color: #ffffff;
    }

    .chat-item-container:nth-child(even) {
      background-color: #f7f7f7;
    }
    .chat-item-container {
      padding: 12px;
      border-bottom: 1px solid #d6d4d4;

      .chat-item-topic {
        &:hover {
          cursor: pointer;
        }
        padding-top: 2px;
        font-size: 13px;
        text-decoration: underline;
        // color: $vind-dark;
      }
    }
  }

  .chat-details {
    width: 62%;
  }
}

.popup-padding {
  padding: 0px;
}

.chat-attachment-download-note {
  margin-top: 6px;
  color: inherit;
  font-size: 12px;
  line-height: 1.35;
  opacity: 0.72;
}

.chat-content {
  :deep(
    .dx-chat-messagebubble:has(.chat-message-attachment-download-expired)
      .dx-chat-file-download-button
  ) {
    display: none;
  }
}
</style>
